import { CHECKBOX_GROUP_ID, RADIO_BUTTON_ID, REPEATING_STRUCTURED_ID, SELECT_LIST_ID } from "../constants/fieldTypes";
import EditableFormField from "../models/pageModels/EditableFormField";
import FieldModel from "../models/pageModels/FieldModel";
import { FieldFile } from "../models/serviceModels/FieldFile";
import FormField from "../models/serviceModels/FormField";
import FormInstanceField from "../models/serviceModels/FormInstanceField";

const parseJson = (value: string): any | undefined => {
    try {
        return JSON.parse(value);
    } catch {
        return undefined;
    }
};

const normalizeSingleOptionFieldValue = (source: FormField, value: string): string => {
    if (!value) {
        return '';
    }

    const parsedValue = parseJson(value);
    if (parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)) {
        return parsedValue.label !== undefined && parsedValue.value !== undefined ? value : '';
    }

    const optionValue = typeof parsedValue === 'string' ? parsedValue : value;
    const matchingOption = source.options?.find(o => o.value?.toString() === optionValue?.toString())
        || source.options?.find(o => o.label?.toString() === optionValue?.toString());

    return JSON.stringify((matchingOption && {
        label: matchingOption.label,
        value: matchingOption.value
    }) || {
        label: optionValue,
        value: optionValue
    });
};

const normalizeMultiOptionFieldValue = (source: FormField, value: string): string => {
    if (!value) {
        return '[]';
    }

    const parsedValue = parseJson(value);
    if (Array.isArray(parsedValue)) {
        return value;
    }

    if (parsedValue && typeof parsedValue === 'object') {
        return parsedValue.value ? JSON.stringify([parsedValue]) : '[]';
    }

    const optionValue = typeof parsedValue === 'string' ? parsedValue : value;
    const matchingOption = source.options?.find(o => o.value?.toString() === optionValue?.toString())
        || source.options?.find(o => o.label?.toString() === optionValue?.toString());

    return JSON.stringify([
        (matchingOption && {
            label: matchingOption.label,
            value: matchingOption.value
        }) || {
            label: optionValue,
            value: optionValue
        }
    ]);
};

const normalizeStructuredCollectionFieldValue = (value: string): string => {
    if (!value) {
        return '[]';
    }

    const parsedValue = parseJson(value);
    if (!Array.isArray(parsedValue)) {
        return '[]';
    }

    return JSON.stringify(parsedValue.map(collection => ({
        ...collection,
        fields: Array.isArray(collection?.fields)
            ? collection.fields.map((field: any) => ({
                ...field,
                value: normalizeFormInstanceFieldValue(field, field.value || '')
            }))
            : []
    })));
};

const normalizeFormInstanceFieldValue = (source: FormField, value = ''): string => {
    switch (source.fieldTypeId) {
        case SELECT_LIST_ID:
        case RADIO_BUTTON_ID:
            return normalizeSingleOptionFieldValue(source, value);
        case CHECKBOX_GROUP_ID:
            return normalizeMultiOptionFieldValue(source, value);
        case REPEATING_STRUCTURED_ID:
            return normalizeStructuredCollectionFieldValue(value);
        default:
            return value;
    }
};

const mapFormFieldToFieldModel = (source: FormField, deleted = false): FieldModel => {
    return {
        ...source,
        options: source.options && source.options.map(o => {
            return {
                ...o,
                deleted
            }
        })
    };
};

const mapFormFieldToEditableFormField = (source: FormField, value = '', instanceFieldId = '', files:FieldFile[] = []): EditableFormField => {
    return {
        ...source,
        value: normalizeFormInstanceFieldValue(source, value),
        instanceFieldId,
        fieldNumber: 0,
        files
    }
};

const mapFormFieldInstanceToEditableFormField = (source: FormInstanceField, formId: string): EditableFormField => {
    return {
        ...source,
        instanceFieldId: source.id,
        value: source.value,
        fieldNumber: 0,
        name: source.name,
        formId
    }
};

export {
    mapFormFieldToFieldModel,
    mapFormFieldToEditableFormField,
    mapFormFieldInstanceToEditableFormField,
    normalizeFormInstanceFieldValue
}
